import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { DateTime } from 'luxon';
import type { ValidationResult } from '../api/mail-content-extraction-agent/2.2-extract-product-information.js';

@Entity()
export class ExtractedProductInfo extends AdornisEntity implements ValidationResult {
  static override _class = 'ExtractedProductInfo';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() }) _id!: string;

  // XML: PURCH_NO_C
  // Bestellnummer des Produkts: (interne Nummer beim Kunden)
  @Field(() => String) requestID: Maybe<string>;

  // Warengruppe
  @Field(() => String) productGroup: Maybe<string>;

  // Werkstoff
  @Field(() => String) material: Maybe<string>;

  // Lieferzustand Bezeichnung
  @Field(() => String) deliveryCondition: Maybe<string>;

  // Außendurchmesser 1
  @Field(() => Float) outerDiameter1: Maybe<number>;
  @Field(() => Float) outerDiameterToleranceUpper: Maybe<number>; // z.B. +0
  @Field(() => Float) outerDiameterToleranceLower: Maybe<number>; // z.B. -0.30

  // Außendurchmesser 2
  @Field(() => Float) outerDiameter2: Maybe<number>;

  // Wanddicke
  @Field(() => Float) wallThickness: Maybe<number>;
  @Field(() => Float) wallThicknessToleranceUpper: Maybe<number>; // z.B. +0.30
  @Field(() => Float) wallThicknessToleranceLower: Maybe<number>; // z.B. -0

  // Innendicke (assumed Inner Diameter)
  @Field(() => Float) innerDiameter: Maybe<number>;

  // Menge
  @Field(() => Int) quantity: Maybe<number>;

  // Länge
  @Field(() => Float) length: Maybe<number>;
  @Field(() => Float) minLength: Maybe<number>; // optional, falls Toleranz angegeben
  @Field(() => Float) maxLength: Maybe<number>; // optional, falls Toleranz angegeben

  // Vom Kunden als reine Gesamtmenge in Längeneinheiten genannte Bestellmenge (mm), wenn KEINE
  // Stücklänge angegeben war (z.B. "200 m"). Wird nach dem Produkt-Match anhand der
  // Handelslänge in Stücklänge × Menge umgerechnet (in einem Stück nicht lieferbar).
  @Field(() => Float) orderTotalLength: Maybe<number>;

  // Produktnummer
  @Field(() => String) productNumber: Maybe<string>;

  // Wunschlieferdatum
  @Field(() => DateTime) desiredDeliveryDate: Maybe<DateTime>;

  // Toleranz
  @Field(() => Float) tolerance: Maybe<number>;

  // Längenart (HL, ML, etc.)
  @Field(() => String) lengthType: Maybe<string>;

  // Explizite Fixlänge im Quelltext ("Fixlänge", "FX", "FL", "fix", "auf Maß")
  @Field(() => Boolean, { default: v => v ?? false }) hasExplicitFixLength: Maybe<boolean>;

  // Explizite Handelslänge im Quelltext ("HL", "Handelslänge", "HL 5-7m")
  @Field(() => Boolean, { default: v => v ?? false }) hasExplicitHandelslaenge: Maybe<boolean>;

  // Zertifikat (Z_22 = 2.2 Werkzeugnis, Z_31 = 3.1 Abnahmeprüfzeugnis)
  @Field(() => String) certificate: Maybe<string>;

  // Positionsnummer
  @Field(() => String) positionNumber: Maybe<string>;

  // Ist eine Alternative
  @Field(() => Boolean) isAlternative: Maybe<boolean>;

  // Alternative für Position
  @Field(() => String) alternativeFor: Maybe<string>;

  @Field(() => Boolean) isValid!: boolean;
  @Field(() => [String]) errors!: string[];
  @Field(() => [String]) warnings!: string[];

  /**
   * The extraction made an assumption it is not sure about (see {@link REVIEW_CODES}). Kept
   * separate from `warnings`, which is the validator's channel: this one asks a question of a
   * person rather than describing a defect.
   */
  @Field(() => Boolean) needsReview: Maybe<boolean>;
  /** German sentences a person reads — the same wording the calculation already produced. */
  @Field(() => [String]) reviewReasons: Maybe<string[]>;
  /** Stable keys for the same reasons, so questions can be generated without parsing prose. */
  @Field(() => [String]) reviewCodes: Maybe<string[]>;

  constructor(doc: PartialEntityData<ExtractedProductInfo>) {
    super(doc);
  }
}

@Entity()
export class ManualCorrectedProductInfo extends ExtractedProductInfo {
  static override _class = 'ManualCorrectedProductInfo';

  @Field(() => String) extractedProductInfoId: Maybe<string>;

  // Marks a position the user removed: it is kept for reversibility but excluded from the
  // effective product set, so it neither blocks nor appears in the SAP export.
  @Field(() => Boolean, { default: v => v ?? false }) removed: Maybe<boolean>;

  get isNewProduct() {
    return !this.extractedProductInfoId;
  }

  constructor(doc: PartialEntityData<ManualCorrectedProductInfo>) {
    super(doc);
  }
}

/**
 * Why a position wants a human look. The German text lives in `reviewReasons`; these keys are
 * what the code matches on, so a reworded message never changes behaviour.
 */
export const REVIEW_CODES = {
  /** No quantity stated at all — a single piece was assumed. */
  QUANTITY_DEFAULTED: 'QUANTITY_DEFAULTED',
  /** total ÷ per-piece is 1–5 % off a whole number; rounded up. */
  QUANTITY_NON_INTEGER: 'QUANTITY_NON_INTEGER',
  /** More than 5 % off — total amount and per-piece length probably disagree. */
  QUANTITY_LARGE_DEVIATION: 'QUANTITY_LARGE_DEVIATION',
  /** The amount carried a unit we cannot convert; a single piece was assumed. */
  QUANTITY_UNIT_UNKNOWN: 'QUANTITY_UNIT_UNKNOWN',
  /** The quantity calculation threw. */
  QUANTITY_CALC_ERROR: 'QUANTITY_CALC_ERROR',
  /** Only a bulk length was given; the piece count is a provisional trade-length estimate. */
  BULK_LENGTH_EXTRAPOLATED: 'BULK_LENGTH_EXTRAPOLATED',
  /** A non-round metre amount was read as the length of one cut piece, not as a bulk order. */
  BULK_READ_AS_CUT_LENGTH: 'BULK_READ_AS_CUT_LENGTH',
} as const;

export type ReviewCode = (typeof REVIEW_CODES)[keyof typeof REVIEW_CODES];

// Human-readable labels for the fields a user can correct. Keys not listed fall back to the raw key.
export const PRODUCT_FIELD_LABELS: Record<string, string> = {
  requestID: 'Bestell-Nr.',
  productGroup: 'Warengruppe',
  material: 'Material',
  deliveryCondition: 'Lieferbedingung',
  outerDiameter1: 'AD 1',
  outerDiameter2: 'AD 2',
  outerDiameterToleranceUpper: 'AD 1 Tol. oben',
  outerDiameterToleranceLower: 'AD 1 Tol. unten',
  wallThickness: 'Wanddicke',
  wallThicknessToleranceUpper: 'Wand Tol. oben',
  wallThicknessToleranceLower: 'Wand Tol. unten',
  innerDiameter: 'ID',
  quantity: 'Menge',
  length: 'Länge',
  minLength: 'Min. Länge',
  maxLength: 'Max. Länge',
  orderTotalLength: 'Gesamtlänge',
  productNumber: 'Mat-Nr',
  desiredDeliveryDate: 'Wunschlieferdatum',
  tolerance: 'Toleranz',
  lengthType: 'Längenart',
  hasExplicitFixLength: 'Explizite Fixlänge',
  hasExplicitHandelslaenge: 'Explizite Handelslänge',
  certificate: 'Zertifikat',
  positionNumber: 'Pos.-Nr.',
  isAlternative: 'Alternative',
  alternativeFor: 'Alternative für',
};

/**
 * Bookkeeping the extraction writes about itself — never a change a person made, so a diff has
 * to skip it. `needsReview`/`reviewReasons`/`reviewCodes` belong here for the same reason
 * `isValid`/`errors`/`warnings` do.
 */
export const PRODUCT_BOOKKEEPING_FIELDS = new Set([
  '_id',
  '_class',
  'extractedProductInfoId',
  'removed',
  'isValid',
  'errors',
  'warnings',
  'needsReview',
  'reviewReasons',
  'reviewCodes',
]);
